<template>
  <div v-if="!isLoading">
    <EntityFiles
      v-if="shouldShowFiles"
      :entityId="entityId"
      :testId="testId"
      :quarter="quarter_name"
    />

    <div v-if="hasResultsData">
      <div v-for="[tableName, tableData] in Object.entries(testResults)">
        <table-component
          :to-csv-args="{
            index: false,
            sep: ';',
            decimal: ','
          }"
          default-order-by="account"
          v-if="
            tableData && !tableData.message && tableName !== 'control_passed'
          "
          :key="tableName"
          :url="API_ENDPOINTS.lazyTable"
          :ref="tableData.key"
          :lazy-table="handleRawTableHiddenColumns(tableData)"
          :headings="getHeadings(tableData)"
          :hidden-columns="hiddenColumns"
          keep-id
          :class="[
            tableData.raw_table ? 'nested-table' : 'compact-table',
            {
              'frozen-columns-table': shouldFreezeFirstTwoColumns,
              'total-ok': isTotalOk,
              'total-row-table': hasTotalRow,
            },
          ]"
          @count-change="freezeFirstTwoColumns()"
          :pivot="totalRowComputed"
          :initFilters="{entity_code__nin: [totalCode]}"
          forceFilter
        >
          <template
            #[controlColName]="{ row }"
            v-for="controlColName in getControlColumns(tableData)"
          >
            <template v-if="getHeadings(tableData)[controlColName] === 'Ecart'">
              <span
                v-if="
                  Object.keys(row).includes(controlColName) && row[controlColName] !== ''
                "
              >
                {{ $getNumericalValue(row[controlColName]) }}
              </span>
              <span v-else>
                {{
                  $getNumericalValue(
                    calculateEcartWithManualInput(
                      row,
                      ECART_COLUMNS[testId]?.[tableName] || controlColName,
                      tableName
                    )
                  )
                }}
              </span>
            </template>
            <template v-else>
              <span
                v-if="performManualControl(row, tableName)"
                :class="performManualControl(row, tableName) === 'KO' ? 'text-danger' : 'text-success'"
              >
                {{ performManualControl(row, tableName) }}
              </span>
              <span v-else :class="getControlClass(row[controlColName])">
                {{ row[controlColName] }}
              </span>
            </template>
          </template>
          <template
            #[balanceTemplateName]="{ row }"
            v-for="balanceTemplateName in [
              'immo_total',
              'Solde',
              'balance',
              'amount_cca',
              'rent_amount',
              'balance_6',
              'balance_7',
              testId == 'D2' ? 'account' : '',
            ]"
          >
            <div
              @click="
                () =>
                  handleClickedRow(
                    { ...row, table_index: tableName },
                    balanceTemplateName
                  )
              "
              class="w-100"
              :class="{'pointer': !Object.keys(DETAIL_TABLE_NAMES).includes(testId) || DETAIL_TABLE_NAMES[testId] != null}"
            >
              {{ $getNumericalValue(row[balanceTemplateName], 2, 0) }}
            </div>
          </template>
          <template v-if="testId == 'B5'" #affectation="{ row }">
            <div @click="() => handleClickedRow(row)" class="pointer w-100">
              {{ row.affectation }}
            </div>
          </template>
          <template
            #[manualInputColumn.templateKey]="{ row }"
            v-for="manualInputColumn in processedManualControlColumns"
          >
            <RaInput
              :modelValue="
                manualControl?.[tableName]?.[row.aggregation_key]?.[
                  manualInputColumn.value
                ] || ''
              "
              :disabled="disabled"
              filter-input
              size="sm"
              @change="
                (newValue, isUpdated) =>
                  handleManualControl(
                    row,
                    manualInputColumn.value,
                    newValue,
                    isUpdated,
                    tableName
                  )
              "
            />
          </template>
          <template #fiscal_year="{ row }">
            <div class="pointer w-100" @click="() => handleClickedRow(row)">
              {{ row.fiscal_year }}
            </div>
          </template>
        </table-component>
        <div v-else-if="tableName !== 'control_passed'">
          <div v-if="tableData && tableData.message" class="alert alert-danger">
            {{ tableData.message }}
          </div>
          <div v-else class="alert alert-danger">
            Results not available for selected test
          </div>
        </div>
      </div>
      <RaModal
        v-if="detailTables && Object.keys(detailTables).length && selectedRow"
        v-model:show="showDetails"
        show-close-button
        title="Details"
        confirmText=""
        cancelText=""
      >
        <div
          v-for="([tableName, tableData], index) in Object.entries(
            detailTables
          )"
        >
          <table-component
            :to-csv-args="{
              index: false,
              sep: ';',
              decimal: ','
            }"
            default-order-by="account"
            add-table-classes="details-modal-table"
            class="compact-table"
            :key="tableName"
            keep-id
            v-if="
              tableData &&
              (restrictedDetailsIndex == null ||
                restrictedDetailsIndex == index)
            "
            :url="API_ENDPOINTS.lazyTable"
            :lazy-table="tableData[0]"
            :headings="getHeadings(tableData)"
            :columns-meta="[
              ...(getColumnsMeta(tableName, testId) || tableData[0].columns),
              ...(MANUAL_CONTROL_DETAIL_COLUMNS[testId] || []),
            ]"
            :hidden-columns="[
              ...(HIDDEN_DETAIL_COLUMNS[testId] || []),
              'aggregation_key',
            ]"
            :init-filters="getInitFilters(tableName)"
            :filter-exclude-columns="getFilteredColumns()"
            forceFilter
            disableSearchParams
            :ref="index"
            displayButtonsRow
          >
            <template
              #[manualInputColumn]="{ row }"
              v-for="manualInputColumn in MANUAL_CONTROL_DETAIL_COLUMNS[
                testId
              ] || []"
            >
              <RaInput
                :modelValue="
                  manualControl?.[tableName.toLowerCase()]?.[index]?.[
                    row.aggregation_key
                  ]?.[manualInputColumn] || ''
                "
                :disabled="disabled"
                filter-input
                size="sm"
                @change="
                  (newValue, isUpdated) =>
                    handleManualControl(
                      row,
                      manualInputColumn,
                      newValue,
                      isUpdated,
                      tableName.toLowerCase(),
                      index
                    )
                "
              />
            </template>
            <template #export_btns>
              <template v-if="$refs[index]">
                <CButton
                  class="ra-button"
                  color="warning"
                  size="sm"
                  v-if="isGeneratingFile"
                  v-c-tooltip="{
                    content: 'Preparing file',
                    placement: 'top-end',
                  }"
                >
                  Generating File
                  <i class="fas fa-spinner spin-animation ml-2"/>
                </CButton>
                <CButton
                  v-else
                  class="ra-button"
                  color="secondary"
                  size="sm"
                  @click="() => generateThenDownloadExport(index)"
                  v-c-tooltip="{
                    content: 'Download export file',
                    placement: 'top-end',
                  }"
                >
                  Export data
                </CButton>
              </template>
            </template>
          </table-component>
        </div>
      </RaModal>
    </div>
    <div v-else-if="!shouldShowFiles" class="alert alert-danger">
      Results not available for selected test
    </div>
  </div>
  <DotsLoader v-else></DotsLoader>
</template>

<script>
import {TestResultMixin} from "@/mixins/testResult";
import {debounce} from "@/utils/helpers"
import EntityFiles from "@/components/closingFile/EntityFiles.vue";

export default {
  name: "ClosingFileTestResult",
  components: { EntityFiles },
  mixins: [TestResultMixin],
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      hiddenColumns: [
        'entity_code',
        'Societe',
        'aggregation_key',
        'supplier_cl_str',
      ],
      isGeneratingFile: false,
      firstColObserver: null
    };
  },
  computed: {
    hasResultsData() {
      const testResultNames =
        Object.keys(this.testResults || {})?.filter(
          (testName) => testName !== "control_passed"
        ) || [];
      return (
        testResultNames?.length &&
        !testResultNames.every((testName) => this.testResults[testName].message)
      );
    },
    processedManualControlColumns() {
      return this.manualControlColumns.flatMap(col => [
        {
          templateKey: 'c__' + col,
          value: col,
        },
        {
          templateKey: col,
          value: col,
        },
      ])
    },
    shouldFreezeFirstTwoColumns() {
      return this.testId === "B3.3"
    },
    shouldShowFiles() {
      return this.testId === "PP";
    },
  },
  methods: {
    freezeFirstTwoColumns() {
      if (!this.shouldFreezeFirstTwoColumns) return;
      requestAnimationFrame(() => {
        let firstColWidth = 0;
        document.querySelectorAll(".frozen-columns-table table tr").forEach(tableRow => {
          const firstCell = Array.from(tableRow.childNodes).find(n => ['TD', 'TH'].includes(n.tagName));
          if (!firstCell) return;

          let secondCell = firstCell.nextSibling;
          while (!["TD", "TH"].includes(secondCell?.tagName)) {
            secondCell = secondCell?.nextSibling
            if (!secondCell) return;
          }

          if (!this.firstColObserver) {
            this.firstColObserver = new ResizeObserver(
              () => debounce(this.freezeFirstTwoColumns, 100)
            )
            this.firstColObserver.observe(firstCell)
          }

          firstCell.style.left = 0
          if (!firstColWidth) firstColWidth = firstCell.getBoundingClientRect().width + "px"
          secondCell.style.left = firstColWidth
        })
      })
    },
    handleRawTableHiddenColumns(tableData) {
      if (tableData.raw_table) {
        tableData = JSON.parse(JSON.stringify(tableData));
        let rawTableColumns = []

        tableData.raw_table.columns.forEach(col => {
          if (!this.hiddenColumns.includes(col.name)) rawTableColumns.push(col)
        })
        rawTableColumns.unshift({name: ""})
        tableData.raw_table.columns = rawTableColumns
      }
      return tableData
    },
    getControlClass(controlValue) {
      const controlColors = {
        "OK": "success",
        "KO": "danger",
        "Déficit": "danger",
        "Bénéfice": "success",
        "A lettrer": "warning",
        "NS": "info",
        "A analyser": "danger",
        "Variable": "info",
      };
      if (controlColors[controlValue])
        return `text-${controlColors[controlValue]}`;
    },
    async generateThenDownloadExport(index) {
      this.isGeneratingFile = true;
      await this.$refs[index][0].generateLazyTableExport();
      await this.downloadExport(index);
    },
    async downloadExport(index) {
      if (
        this.$refs[index][0].lazyTableExportExists &&
        this.$refs[index][0].lazyTableExportFilename
      ) {
        await this.$refs[index][0].downloadExport();
        this.isGeneratingFile = false;
        return;
      }
      setTimeout(() => this.downloadExport(index), 1000);
    },
  },
};
</script>

<style lang="scss">
.frozen-columns-table.ra-table table.mz-table.table.VueTables__table {
  position: relative;

  tr.VueTables__row td:nth-child(-n + 2) {
    position: sticky;
    background-color: white !important;
    z-index: 2;
  }

  thead tr th:nth-child(-n + 2) {
    position: sticky !important;
    background-color: white !important;
    z-index: 2;
  }

  td, th {
    &:nth-child(2) {
      border-right: 1px solid map-deep-get($colors, "neutral", "light") !important;
    }
  }
}

.nested-table {
  .table .VueTables__child-row td {
    max-width: 200px;
  }

  .VueTables__row {
    td {
      .text-right {
        text-align: unset !important;
      }

      word-break: break-word;
    }
  }

  thead {
    z-index: 4;
  }

  .nested-rows {
    thead {
      height: 1px !important;
    }

    .table-responsive {
      overflow: hidden;
    }

    .mz-table {
      padding-left: unset;
    }
  }

  .sublevel-scroll {
    thead {
      position: sticky;
      top: 0;
      background-color: white;
      z-index: 1;
    }
  }
}

.ra-modal__body .ra-table .buttons-row {
  display: flex !important;
  justify-content: flex-end;
}

.ra-modal__body .number-of-records {
  display: none !important;
}

.details-modal-table {
  height: 100%;

  .VueTables {
    max-height: calc(80vh - 200px) !important;
  }
}

.compact-table.ra-table table.mz-table {
  .VueTables__filters-row {
    height: 50px !important;

    .ra-input.input-sm.filter-input .input-group-wrapper {
      min-width: 120px !important;
    }
  }

  thead tr th {
    min-width: 0 !important;
    padding: 3px 6px !important;
  }

  .VueTables__column-filter {
    min-width: 120px !important;
  }

  tr.VueTables__row, tr.pivot-row {
    &, td {
      padding: 3px 6px !important;
      min-height: 32px !important;
      height: 32px;
      min-width: 0 !important;
    }

    .ra-input .input-group-wrapper {
      padding: 3px 10px;
      height: 28px;
    }
  }
}

.total-row-table.ra-table .table-responsive {
  $header-width: 170px;

  th {
    &:first-child, &:nth-child(2) {
      width: $header-width;
      min-width: $header-width !important;
    }
  }

  .pivot-row {
    background: $gray-50;

    td:last-child {
      color: $danger;
    }

    td:first-child {
      width: $header-width * 2;
      position: absolute;
      text-align: center;
    }
  }
}

.total-ok .pivot-row td:last-child {
  color: $success !important;
}
</style>
